/**
 * Inline frame reference rendered inside a comment bubble when an
 * agent's reply produced or updated a frame. Clicking opens the frame.
 */
import { getFrameTypeIcon } from "@/components/ui/concept-icons";
import { cn } from "@/lib/cn";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";

interface FrameChipProps {
  frameId: string;
  title: string;
  frameType: string;
  onPan: (frameId: string) => void;
  className?: string;
}

export function FrameChip({
  frameId,
  title,
  frameType,
  onPan,
  className,
}: FrameChipProps) {
  const FrameIcon = getFrameTypeIcon(frameType);
  const handleClick = () => {
    if (activateFrameInCurrentContext(frameId)) return;
    onPan(frameId);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`frame-chip-${frameId}`}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-control " +
          "border border-glass-stroke bg-glass-fill px-2 py-1 text-appkit-mini " +
          "text-secondary transition-colors",
        className,
      )}
    >
      <FrameIcon className="h-3 w-3 shrink-0 text-muted" />
      <span className="truncate">{title}</span>
      <span className="shrink-0 text-appkit-mini uppercase tracking-wide text-muted">
        {frameType}
      </span>
    </button>
  );
}
